import { z } from 'zod';
import {
  CoasterProjectV1Schema,
  CoasterProjectV2Schema,
  CoasterProjectV3Schema,
  CoasterProjectV4Schema,
  CoasterProjectV5Schema,
  CoasterProjectV6Schema,
  CoasterProjectV7Schema,
  createUnvalidatedValidationSummary,
  type CoasterProjectV1,
  type CoasterProjectV2,
  type CoasterProjectV3,
  type CoasterProjectV4,
  type CoasterProjectV5,
  type CoasterProjectV6,
  type CoasterProjectV7,
} from './project';

const Vec3V0Schema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  z: z.number().finite(),
  bankDegrees: z.number().finite().optional(),
});

/** Test-only legacy shape used to prove the migration boundary before real releases exist. */
export const CoasterProjectV0Schema = z.object({
  schemaVersion: z.literal(0),
  id: z.string().min(1),
  title: z.string().min(1),
  savedAt: z.iso.datetime({ offset: true }),
  closed: z.boolean(),
  points: z.array(Vec3V0Schema),
});

export type CoasterProjectV0 = z.infer<typeof CoasterProjectV0Schema>;

export type ProjectLoadErrorCode =
  | 'invalid_json'
  | 'missing_schema_version'
  | 'unsupported_newer_schema'
  | 'unsupported_older_schema'
  | 'migration_failed'
  | 'validation_failed';

export type ProjectLoadResult =
  | { ok: true; project: CoasterProjectV7; migratedFrom: 0 | 1 | 2 | 3 | 4 | 5 | 6 | null }
  | {
      ok: false;
      error: { code: ProjectLoadErrorCode; message: string; recoverable: true; details?: unknown };
    };

export function migrateV0ToV1(value: CoasterProjectV0): CoasterProjectV1 {
  const nodes = value.points.map((point, index) => {
    return {
      id: `node:${index}`,
      position: { x: point.x, y: point.y, z: point.z },
      bankDegrees: point.bankDegrees ?? 0,
    };
  });
  const sectionCount = value.closed ? nodes.length : Math.max(0, nodes.length - 1);
  const sections = Array.from({ length: sectionCount }, (_, index) => ({
    id: `section:${index}`,
    fromNodeId: nodes[index]!.id,
    toNodeId: nodes[(index + 1) % nodes.length]!.id,
    behavior:
      index === 0
        ? ({ kind: 'station', holdSeconds: 2, dispatchSpeedMps: 2 } as const)
        : ({ kind: 'normal' } as const),
  }));

  return CoasterProjectV1Schema.parse({
    schemaVersion: 1,
    projectId: value.id,
    revisionId: `${value.id}:migrated-v1`,
    coasterFamilyId: 'steel.standard',
    metadata: {
      title: value.title,
      description: '',
      createdAt: value.savedAt,
      updatedAt: value.savedAt,
      creator: { kind: 'local', id: null, displayName: 'Local creator' },
      tags: [],
    },
    plot: {
      shape: 'rectangle',
      sizeMeters: { x: 120, z: 90 },
      origin: { x: 0, y: 0, z: 0 },
      groundElevationMeters: 0,
    },
    track: { splineKind: 'cubic_bspline', degree: 3, closed: value.closed, nodes, sections },
    train: {
      familyId: 'steel.standard',
      styleId: 'train.standard',
      carCount: 5,
      carSpacingMeters: 2.4,
      emptyMassKg: 4_000,
      capacityPerCar: 4,
    },
    appearance: {
      environmentThemeId: 'park_model',
      trackColor: '#2457b2',
      supportColor: '#343a40',
      trainColor: '#d94a38',
      stationColor: '#d8d2c5',
      trackMaterialId: 'painted_steel',
      supportMaterialId: 'painted_steel',
      trainMaterialId: 'painted_steel',
    },
    supports: { mode: 'automatic', suppressedSectionIds: [], manual: [] },
    scenery: [],
    highlights: [],
    permissions: { visibility: 'private', allowReadOnlyShare: false, allowRemix: false },
    validation: { status: 'not_run', lastRunAt: null, validatorVersion: 'unvalidated', issues: [] },
    heroCamera: {
      position: { x: 100, y: 90, z: 100 },
      target: { x: 0, y: 10, z: 0 },
      verticalFovDegrees: 45,
      nearMeters: 0.1,
      farMeters: 1_000,
      safeMargin: 0.08,
    },
    lineage: null,
  });
}

export function migrateV1ToV2(value: CoasterProjectV1): CoasterProjectV2 {
  return CoasterProjectV2Schema.parse({
    ...value,
    schemaVersion: 2,
  });
}

/**
 * Phase 2 only stored editor estimates. They are intentionally invalidated
 * during migration because they are not evidence from the authoritative
 * fixed-step full-ride validator introduced in Phase 3.
 */
export function migrateV2ToV3(value: CoasterProjectV2): CoasterProjectV3 {
  return CoasterProjectV3Schema.parse({
    ...value,
    schemaVersion: 3,
    validation: createUnvalidatedValidationSummary(),
  });
}

/** Preserve the exact authored orientation while separating safe local bank
 * from the unwrapped winding used by inversion presets. */
export function migrateV3ToV4(value: CoasterProjectV3): CoasterProjectV4 {
  return CoasterProjectV4Schema.parse({
    ...value,
    schemaVersion: 4,
    track: {
      ...value.track,
      nodes: value.track.nodes.map((node) => ({
        ...node,
        bankDegrees: Math.abs(node.bankDegrees) <= 90 ? node.bankDegrees : 0,
        rollWindingDegrees: Math.abs(node.bankDegrees) <= 90 ? 0 : node.bankDegrees,
      })),
    },
    validation: createUnvalidatedValidationSummary(),
  });
}

/** Preserve legacy geometry exactly while introducing editor-only semantic groups. */
export function migrateV4ToV5(value: CoasterProjectV4): CoasterProjectV5 {
  return CoasterProjectV5Schema.parse({
    ...value,
    schemaVersion: 5,
    editor: {
      pieceGroups: value.track.sections.map((section, index) => ({
        id: `piece:migrated:${index}`,
        definitionId: 'custom',
        sectionIds: [section.id],
        parameters: null,
      })),
    },
  });
}

/**
 * V1-V5 geometry remains byte-for-byte authored data. V6 only adds an
 * explicit legacy geometry discriminator so new modular projects can coexist
 * without silently reconstructing old rides.
 */
export function migrateV5ToV6(value: CoasterProjectV5): CoasterProjectV6 {
  return CoasterProjectV6Schema.parse({
    ...value,
    schemaVersion: 6,
    track: {
      ...value.track,
      geometryKind: 'legacy_cubic_bspline',
    },
  });
}

/**
 * V7 adds the authored-grid builder as a third geometry path. Existing V6
 * projects keep their exact track objects; no spline or modular piece is
 * reconstructed.
 */
export function migrateV6ToV7(value: CoasterProjectV6): CoasterProjectV7 {
  return CoasterProjectV7Schema.parse({
    ...value,
    schemaVersion: 7,
  });
}

export function loadCoasterProject(input: string | unknown): ProjectLoadResult {
  let value: unknown = input;
  if (typeof input === 'string') {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return {
        ok: false,
        error: {
          code: 'invalid_json',
          message: 'This coaster file is not valid JSON.',
          recoverable: true,
        },
      };
    }
  }
  if (!value || typeof value !== 'object' || !('schemaVersion' in value))
    return {
      ok: false,
      error: {
        code: 'missing_schema_version',
        message: 'This coaster file has no schema version.',
        recoverable: true,
      },
    };
  const version = (value as { schemaVersion?: unknown }).schemaVersion;
  if (typeof version !== 'number' || !Number.isInteger(version))
    return {
      ok: false,
      error: {
        code: 'missing_schema_version',
        message: 'This coaster file has an invalid schema version.',
        recoverable: true,
      },
    };
  if (version > 7)
    return {
      ok: false,
      error: {
        code: 'unsupported_newer_schema',
        message: `This coaster requires a newer game version (schema ${version}).`,
        recoverable: true,
      },
    };
  if (version < 0)
    return {
      ok: false,
      error: {
        code: 'unsupported_older_schema',
        message: `Schema ${version} is no longer supported.`,
        recoverable: true,
      },
    };
  if (version === 0) {
    const parsed = CoasterProjectV0Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'migration_failed',
          message: 'This legacy coaster could not be migrated.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    try {
      return {
        ok: true,
        project: migrateV6ToV7(
          migrateV5ToV6(
            migrateV4ToV5(migrateV3ToV4(migrateV2ToV3(migrateV1ToV2(migrateV0ToV1(parsed.data))))),
          ),
        ),
        migratedFrom: 0,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'migration_failed',
          message: 'This legacy coaster could not be migrated.',
          recoverable: true,
          details: error,
        },
      };
    }
  }
  if (version === 1) {
    const parsed = CoasterProjectV1Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    try {
      return {
        ok: true,
        project: migrateV6ToV7(
          migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(migrateV2ToV3(migrateV1ToV2(parsed.data))))),
        ),
        migratedFrom: 1,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'migration_failed',
          message: 'This coaster could not be upgraded to the current editor.',
          recoverable: true,
          details: error,
        },
      };
    }
  }
  if (version === 2) {
    const parsed = CoasterProjectV2Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    try {
      return {
        ok: true,
        project: migrateV6ToV7(
          migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(migrateV2ToV3(parsed.data)))),
        ),
        migratedFrom: 2,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'migration_failed',
          message: 'This coaster could not be upgraded to the current editor.',
          recoverable: true,
          details: error,
        },
      };
    }
  }

  if (version === 3) {
    const parsed = CoasterProjectV3Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    try {
      return {
        ok: true,
        project: migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(migrateV3ToV4(parsed.data)))),
        migratedFrom: 3,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'migration_failed',
          message: 'This coaster could not be upgraded to the current editor.',
          recoverable: true,
          details: error,
        },
      };
    }
  }

  if (version === 4) {
    const parsed = CoasterProjectV4Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    return {
      ok: true,
      project: migrateV6ToV7(migrateV5ToV6(migrateV4ToV5(parsed.data))),
      migratedFrom: 4,
    };
  }

  if (version === 5) {
    const parsed = CoasterProjectV5Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    return { ok: true, project: migrateV6ToV7(migrateV5ToV6(parsed.data)), migratedFrom: 5 };
  }

  if (version === 6) {
    const parsed = CoasterProjectV6Schema.safeParse(value);
    if (!parsed.success)
      return {
        ok: false,
        error: {
          code: 'validation_failed',
          message: 'This coaster file is damaged or invalid.',
          recoverable: true,
          details: parsed.error.issues,
        },
      };
    return { ok: true, project: migrateV6ToV7(parsed.data), migratedFrom: 6 };
  }

  const parsed = CoasterProjectV7Schema.safeParse(value);
  if (!parsed.success)
    return {
      ok: false,
      error: {
        code: 'validation_failed',
        message: 'This coaster file is damaged or invalid.',
        recoverable: true,
        details: parsed.error.issues,
      },
    };
  return { ok: true, project: parsed.data, migratedFrom: null };
}
